import Button from "app/storybookComponents/Button";
import React, { useCallback, useMemo, useState, useRef } from "react";
import { useAppSelector } from "utils/redux/hooks";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import { selectTeamsByTeamId } from "app/containers/Global/slice";
import { Card, Nav } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  SelectedCheckInBreakdownEntity,
  AllWeeklyCheckInTableData,
  WeeklyCheckinEmailReportDataByTimeAndType,
} from "../types";
import { WeeklyCheckInInitialEntityType } from "app/containers/AdminConsole/constants";
import { responseStatus } from "utils/types";
import CheckInActivityTableContent, {
  CheckInActivityTableContentRef,
} from "./CheckInActivityTableContent";
import CheckInActivityEmailReportsTable from "./CheckInActivityEmailReportsTable";
import ExportCSVButton from "app/storybookComponents/Button/ExportCSVButton";

interface Props {
  getWeeklyCheckInTableDataStatus: responseStatus;
  getWeeklyCheckInActivityEmailReportsTableDataStatus: responseStatus;
  allWeeklyCheckInTableData: AllWeeklyCheckInTableData;
  fetchCheckInCompletionTableData?: (params: {
    entityType: string;
    timeInterval: string;
  }) => void;
  title?: string;
  headerDescription?: string;
  fallbackBackButton?: React.ReactNode;
  hideDepartmentDropdown?: boolean;
  hideEntityDropdown?: boolean;
  isIndividualContributor?: boolean;
  weeklyCheckinEmailReportDataByTimeAndType?: WeeklyCheckinEmailReportDataByTimeAndType;
  fetchCheckInEmailReportsTableData?: (params: {
    reportType: string;
    timeInterval: string;
  }) => void;
  incompleteUserAccountIds?: number[];
  isSample?: boolean;
  showCompanyColumn?: boolean;
  headerCornerContent?: React.ReactNode;
  additionalContentAboveTable?: React.ReactNode;
  headerAboveContent?: React.ReactNode;
}

const CheckInActivityTable = ({
  getWeeklyCheckInActivityEmailReportsTableDataStatus,
  getWeeklyCheckInTableDataStatus,
  allWeeklyCheckInTableData,
  fetchCheckInCompletionTableData,
  fetchCheckInEmailReportsTableData,
  title = "Check-in Activity",
  headerDescription = "Showing activity in this entire organization",
  fallbackBackButton,
  hideDepartmentDropdown = false,
  hideEntityDropdown,
  isIndividualContributor,
  weeklyCheckinEmailReportDataByTimeAndType,
  incompleteUserAccountIds,
  isSample,
  showCompanyColumn,
  headerCornerContent,
  additionalContentAboveTable,
  headerAboveContent,
}: Readonly<Props>) => {
  const tableContentRef = useRef<CheckInActivityTableContentRef>(null);

  const allTeamsById = useAppSelector(selectTeamsByTeamId);
  const departments = useAppSelector(selectDepartments);

  const [entitySelected, setEntitySelected] = useState(
    WeeklyCheckInInitialEntityType
  );
  const [breakdownRowSelectedArray, setBreakdownRowSelectedArray] = useState<
    SelectedCheckInBreakdownEntity[]
  >([]);
  const [visibilityActiveTab, setVisibilityActiveTab] =
    useState<string>("Completion");

  const breakdownRowSelected = useMemo(
    () => breakdownRowSelectedArray[0] ?? null,
    [breakdownRowSelectedArray]
  ) as SelectedCheckInBreakdownEntity | null;

  const getDateString = (startDate: string, endDate?: string) => {
    const startDateObj = new Date(startDate);
    const startDisplayValue = startDateObj.toLocaleDateString("en-US", {
      month: endDate ? "short" : "long",
      day: "numeric",
    });

    // Guard clause for single date
    if (!endDate) {
      return startDisplayValue;
    }

    // If endDate is provided, format as date range
    const endDateObj = new Date(endDate);
    const endDateString = endDateObj.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const endYearString = endDateObj.getFullYear();
    return `${startDisplayValue} - ${endDateString}, ${endYearString}`;
  };

  const getTitleSection = () => {
    if (!breakdownRowSelected) {
      return (
        <div className="d-flex justify-content-between align-items-center">
          <div className="column-gap-8px">
            <h2>{title}</h2>
            <p>{headerDescription}</p>
          </div>
          {headerCornerContent}
        </div>
      );
    }

    const dataRangeString = getDateString(
      breakdownRowSelected.startDate,
      breakdownRowSelected.endDate
    );

    const entitySelectedText = getEntitySelectedText();
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="column-gap-8px">
          <h2>Completion Activity Breakdown</h2>
          <div className="row-gap-4px grey-text">
            <p>
              Check-In activity for {entitySelectedText} from {dataRangeString}
            </p>
          </div>
        </div>
        <ExportCSVButton
          getRows={() => {
            return tableContentRef.current?.exportCSV() ?? [];
          }}
          headers={[
            { label: "Name", key: entitySelected },
            { label: "Date", key: "date" },
            { label: "Overall Score", key: "overallScore" },
            { label: "Mood Score", key: "moodScore" },
            { label: "Progress Score", key: "progressScore" },
            { label: "Readiness Score", key: "readinessScore" },
          ]}
        />
      </div>
    );
  };

  const getEntitySelectedText = () => {
    if (!breakdownRowSelected) {
      return "";
    }

    if (breakdownRowSelected.entityType === "department") {
      const departmentName =
        departments[breakdownRowSelected.entityId]?.name ?? "";
      return departmentName.toLowerCase().includes("department")
        ? departmentName
        : `${departmentName} Department`;
    }

    const teamName = allTeamsById[breakdownRowSelected.entityId]?.teamName;
    return teamName.toLowerCase().includes("team")
      ? teamName
      : `${teamName} Team`;
  };

  const onBackNavigate = () => {
    const newBreakdownRowSelectedArray = [...breakdownRowSelectedArray];
    const removedSelectedRow = newBreakdownRowSelectedArray.shift();

    if (removedSelectedRow) {
      setEntitySelected(removedSelectedRow.entityType);
    }
    setBreakdownRowSelectedArray(newBreakdownRowSelectedArray);
  };

  const getBackButton = () => {
    if (!breakdownRowSelected) {
      return fallbackBackButton ?? null;
    }

    return (
      <Button
        variant="secondary-blue"
        style={{ border: "none", marginBottom: "20px" }}
        onClick={() => {
          onBackNavigate();
        }}
      >
        <FontAwesomeIcon icon="arrow-left" className="me-2" /> Back
      </Button>
    );
  };

  const setBreakdownRowSelected = useCallback(
    (breakdownRowSelected: SelectedCheckInBreakdownEntity) => {
      setBreakdownRowSelectedArray([
        breakdownRowSelected,
        ...breakdownRowSelectedArray,
      ]);
    },
    [setBreakdownRowSelectedArray, breakdownRowSelectedArray]
  );

  const getNav = () => {
    if (breakdownRowSelected || isIndividualContributor) {
      return null;
    }
    return (
      <Nav
        className="simple-nav"
        activeKey={visibilityActiveTab}
        onSelect={(e) => {
          setVisibilityActiveTab(e ?? "Completion");
        }}
      >
        <Nav.Item>
          <Nav.Link eventKey="Completion">Completion</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="Email Reports">Email Reports</Nav.Link>
        </Nav.Item>
      </Nav>
    );
  };

  const getTableContent = () => {
    if (visibilityActiveTab === "Completion") {
      return (
        <CheckInActivityTableContent
          ref={tableContentRef}
          getWeeklyCheckInTableDataStatus={getWeeklyCheckInTableDataStatus}
          allWeeklyCheckInTableData={allWeeklyCheckInTableData}
          fetchTableData={fetchCheckInCompletionTableData}
          hideDepartmentDropdown={hideDepartmentDropdown}
          hideEntityDropdown={hideEntityDropdown}
          isIndividualContributor={isIndividualContributor}
          breakdownRowSelected={breakdownRowSelected}
          setBreakdownRowSelected={setBreakdownRowSelected}
          setEntitySelected={setEntitySelected}
          entitySelected={entitySelected}
          incompleteUserAccountIds={incompleteUserAccountIds}
          isSample={isSample}
          showCompanyColumn={showCompanyColumn}
        />
      );
    }
    return (
      <CheckInActivityEmailReportsTable
        getWeeklyCheckInActivityEmailReportsTableDataStatus={
          getWeeklyCheckInActivityEmailReportsTableDataStatus
        }
        fetchTableData={fetchCheckInEmailReportsTableData}
        weeklyCheckinEmailReportDataByTimeAndType={
          weeklyCheckinEmailReportDataByTimeAndType
        }
        isSample={isSample}
      />
    );
  };

  return (
    <>
      <div>{getBackButton()}</div>
      <Card className="weekly-checkin-activity-table-card">
        {headerAboveContent}
        {getTitleSection()}
        {getNav()}
        {additionalContentAboveTable}
        {getTableContent()}
      </Card>
    </>
  );
};

export default CheckInActivityTable;
